/**
 * PageLoadingState — Standardised full-page loading visualisation.
 *
 * Rendered whenever a page is hydrating data for the first time. Provides a
 * consistent, polished loading experience across the entire Stream platform:
 *
 *  - Centred branded card with glassmorphism
 *  - Animated streaming dots + orbit ring
 *  - Optional contextual label (defaults to "Loading…")
 *  - Fade-in entrance animation
 *
 * Usage:
 * ```tsx
 * if (loading && !data) return <PageLoadingState label="Loading customers…" />
 * ```
 *
 * Also used as the `Suspense` fallback for lazy-loaded routes in routes.tsx.
 */

import { type CSSProperties, useEffect, useState } from 'react'

export interface PageLoadingStateProps {
  /** Contextual label shown below the animation. */
  label?: string
  /** Optional secondary hint. */
  hint?: string
}

export function PageLoadingState({
  label = 'Loading…',
  hint,
}: PageLoadingStateProps) {
  // Delayed entrance to avoid flashing on fast loads
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      style={{
        ...styles.container,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.98)',
      }}
    >
      <div style={styles.card}>
        {/* Orbit ring animation */}
        <div style={styles.orbitContainer}>
          <svg width="64" height="64" viewBox="0 0 64 64" style={styles.orbitSvg}>
            {/* Track ring */}
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="var(--border-subtle)"
              strokeWidth="1.5"
            />
            {/* Animated arc */}
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="var(--accent-primary)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="44 132"
              className="page-loading-orbit"
            />
          </svg>

          {/* Centre dot cluster */}
          <div style={styles.centreCluster}>
            <div className="streaming-dots" style={{ transform: 'scale(1.1)' }}>
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>

        {/* Label */}
        <span style={styles.label}>{label}</span>

        {/* Optional hint */}
        {hint && <span style={styles.hint}>{hint}</span>}
      </div>
    </div>
  )
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: '320px',
    transition: 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    padding: '48px 56px',
    background: 'var(--surface-card-soft, var(--bg-secondary))',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-xl, 16px)',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px var(--border-subtle)',
  },
  orbitContainer: {
    position: 'relative',
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orbitSvg: {
    position: 'absolute',
    inset: 0,
  },
  centreCluster: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    letterSpacing: '-0.005em',
  },
  hint: {
    fontSize: '12px',
    fontWeight: 400,
    color: 'var(--text-muted)',
    letterSpacing: '0.01em',
    marginTop: '-8px',
  },
}

export default PageLoadingState
